import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { extractErrorMessage } from '../../api/errors';
import { useBrevoFolders, useBrevoLists } from '../../queries/brevoLists';
import { SmartListFilter, SmartListPreviewResult, usePreviewSmartList, usePushSmartList } from '../../queries/smartLists';
import { SmartListPreviewDialog } from './SmartListPreviewDialog';
import { Button } from '../../components/ui/Button';
import { Checkbox } from '../../components/ui/Checkbox';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';

// The billing statuses the Subscriptions page shows, which is what the filter
// now derives per user. The old values ('active', 'inactive', 'suspended',
// 'cancelled') described users.subscription_status, a column nothing
// maintains - it reads 'inactive' for every user, so those filters selected
// everybody or nobody.
const STATUS_OPTIONS = ['Active', 'Past Due', 'Trialing', 'Expired', 'Cancelled', 'Unpaid', 'Promotional'];

// Same six presets as the old admin - no midweek-tips template exists there
// either (there's no midweek column on users, only weekly_tips_subscribed).
const TEMPLATES: { label: string; filter: SmartListFilter }[] = [
  { label: 'Recently Registered Free Users', filter: { registeredWithinDays: 30, hasNoActiveSubscription: true } },
  { label: 'Active Subscribers', filter: { subscriptionStatus: ['Active', 'Trialing'] } },
  { label: 'Lapsed / Cancelled Users', filter: { subscriptionStatus: ['Expired', 'Cancelled'] } },
  { label: 'Marketing Consent Opt-ins', filter: { marketingConsent: true } },
  { label: 'Weekly Tips Subscribers', filter: { weeklyTipsSubscribed: true } },
  // Never subscribed is the absence of any subscription, not a status value:
  // it previously asked for status 'inactive', which every user carried.
  { label: 'Never Subscribed', filter: { neverSubscribed: true } },
];

function tristateSelect(value: boolean | undefined, onChange: (value: boolean | undefined) => void, subscribedLabel: string, notLabel: string) {
  return (
    <select
      value={value === undefined ? 'any' : String(value)}
      onChange={(e) => onChange(e.target.value === 'any' ? undefined : e.target.value === 'true')}
      className="rounded-md border border-input bg-background px-2 py-1.5 text-sm"
    >
      <option value="any">Any</option>
      <option value="true">{subscribedLabel}</option>
      <option value="false">{notLabel}</option>
    </select>
  );
}

export function SmartListsPage() {
  const [filter, setFilter] = useState<SmartListFilter>({});
  const [previewResult, setPreviewResult] = useState<SmartListPreviewResult | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  const [pushMode, setPushMode] = useState<'new' | 'existing'>('new');
  const [newListName, setNewListName] = useState('');
  const [folderId, setFolderId] = useState<number | ''>('');
  const [selectedListId, setSelectedListId] = useState<number | ''>('');
  const [pushError, setPushError] = useState<string | null>(null);
  const [pushSuccessMessage, setPushSuccessMessage] = useState<string | null>(null);
  const [confirmingPush, setConfirmingPush] = useState(false);

  const { data: lists = [] } = useBrevoLists({ all: true });
  const { data: folders = [] } = useBrevoFolders();
  const preview = usePreviewSmartList();
  const push = usePushSmartList();

  const updateFilter = (next: SmartListFilter) => {
    setFilter(next);
    setPreviewResult(null);
    setPushSuccessMessage(null);
  };

  const toggleStatus = (status: string) => {
    const current = filter.subscriptionStatus ?? [];
    const next = current.includes(status) ? current.filter((s) => s !== status) : [...current, status];
    updateFilter({ ...filter, subscriptionStatus: next.length > 0 ? next : undefined });
  };

  const handlePreview = () => {
    setPreviewError(null);
    preview.mutate(filter, {
      onSuccess: (result) => {
        setPreviewResult(result);
        setShowPreview(true);
      },
      onError: (err) => setPreviewError(extractErrorMessage(err, 'Preview failed.')),
    });
  };

  const canPush =
    previewResult !== null && (pushMode === 'new' ? newListName.trim() && folderId !== '' : selectedListId !== '');

  const pushTarget = pushMode === 'new' ? `new list "${newListName.trim()}"` : lists.find((l) => l.id === selectedListId)?.name ?? 'the selected list';

  const doPush = () => {
    if (!previewResult) return;
    setConfirmingPush(false);
    setPushError(null);
    setPushSuccessMessage(null);
    push.mutate(
      pushMode === 'new'
        ? { filter, newListName: newListName.trim(), folderId: folderId === '' ? undefined : folderId }
        : { filter, brevoListId: selectedListId === '' ? undefined : selectedListId },
      {
        onSuccess: (result) => {
          setPushSuccessMessage(
            `Done — ${result.added} contacts added to list #${result.listId}${result.failed > 0 ? `, ${result.failed} failed` : ''}.` +
              (result.excludedWithoutConsent > 0 ? ` ${result.excludedWithoutConsent} without marketing consent were left out.` : ''),
          );
        },
        onError: (err) => setPushError(extractErrorMessage(err, 'Push failed.')),
      },
    );
  };

  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-border p-4">
        <h2 className="mb-3 text-sm font-semibold">Quick templates</h2>
        <div className="flex flex-wrap gap-2">
          {TEMPLATES.map((template) => (
            <button
              key={template.label}
              type="button"
              onClick={() => updateFilter(template.filter)}
              className="rounded-full border border-input px-3 py-1 text-xs font-medium hover:bg-accent"
            >
              {template.label}
            </button>
          ))}
        </div>
      </div>

      <div className="rounded-lg border border-border p-4">
        <h2 className="mb-3 text-sm font-semibold">Custom filter</h2>
        <div className="space-y-3">
          <div>
            <p className="mb-1 text-xs font-medium text-muted-foreground">Subscription status</p>
            <div className="flex flex-wrap gap-2">
              {STATUS_OPTIONS.map((status) => (
                <button
                  key={status}
                  type="button"
                  onClick={() => toggleStatus(status)}
                  className={`rounded-full border px-2.5 py-1 text-xs font-medium ${
                    (filter.subscriptionStatus ?? []).includes(status)
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-input text-muted-foreground hover:border-foreground/30'
                  }`}
                >
                  {status}
                </button>
              ))}
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={filter.hasNoActiveSubscription ?? false}
              onChange={(e) => updateFilter({ ...filter, hasNoActiveSubscription: e.target.checked || undefined })}
            />
            Has no active subscription
          </label>

          <div className="flex items-center gap-2 text-sm">
            <span className="w-40 text-muted-foreground">Marketing consent</span>
            {tristateSelect(filter.marketingConsent, (v) => updateFilter({ ...filter, marketingConsent: v }), 'Opted in', 'Not opted in')}
          </div>

          <div className="flex items-center gap-2 text-sm">
            <span className="w-40 text-muted-foreground">Weekly tips subscription</span>
            {tristateSelect(filter.weeklyTipsSubscribed, (v) => updateFilter({ ...filter, weeklyTipsSubscribed: v }), 'Subscribed', 'Not subscribed')}
          </div>

          <div className="flex items-center gap-2 text-sm">
            <span className="w-52 text-muted-foreground">Registered within the last (days)</span>
            <input
              type="number"
              min={1}
              value={filter.registeredWithinDays ?? ''}
              onChange={(e) => updateFilter({ ...filter, registeredWithinDays: e.target.value ? Number(e.target.value) : undefined })}
              className="w-24 rounded-md border border-input bg-background px-2 py-1 text-sm"
            />
          </div>

          <div className="flex items-center gap-2 text-sm">
            <span className="w-52 text-muted-foreground">Registered more than (days) ago</span>
            <input
              type="number"
              min={1}
              value={filter.registeredBeforeDays ?? ''}
              onChange={(e) => updateFilter({ ...filter, registeredBeforeDays: e.target.value ? Number(e.target.value) : undefined })}
              className="w-24 rounded-md border border-input bg-background px-2 py-1 text-sm"
            />
          </div>
        </div>

        <div className="mt-4 flex items-center gap-3">
          <button
            type="button"
            onClick={handlePreview}
            disabled={preview.isPending}
            className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
          >
            {preview.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Preview Matches
          </button>
          {previewResult && (
            <button type="button" onClick={() => setShowPreview(true)} className="text-sm text-primary hover:underline">
              {previewResult.count} matching users — view list
            </button>
          )}
        </div>
        {previewError && <p className="mt-2 text-sm text-destructive">{previewError}</p>}
      </div>

      <div className="rounded-lg border border-border p-4">
        <h2 className="mb-1 text-sm font-semibold">Push to Brevo list</h2>
        <p className="mb-3 text-xs text-muted-foreground">Only customers who have opted in to marketing are pushed.</p>
        <div className="space-y-3">
          <label className="flex items-center gap-2 text-sm">
            <input type="radio" checked={pushMode === 'new'} onChange={() => setPushMode('new')} />
            Create new list
          </label>
          {pushMode === 'new' && (
            <div className="ml-6 flex items-center gap-2">
              <input
                value={newListName}
                onChange={(e) => setNewListName(e.target.value)}
                placeholder="New list name, e.g. Recently Registered Free Users – Apr 2026"
                className="min-w-0 flex-1 rounded-md border border-input bg-background px-2 py-1.5 text-sm"
              />
              <select
                aria-label="Folder"
                value={folderId}
                onChange={(e) => setFolderId(e.target.value ? Number(e.target.value) : '')}
                className="rounded-md border border-input bg-background px-2 py-1.5 text-sm"
              >
                <option value="">Select a folder…</option>
                {folders.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <label className="flex items-center gap-2 text-sm">
            <input type="radio" checked={pushMode === 'existing'} onChange={() => setPushMode('existing')} />
            Add to existing list
          </label>
          {pushMode === 'existing' && (
            <select
              aria-label="Existing list"
              value={selectedListId}
              onChange={(e) => setSelectedListId(e.target.value ? Number(e.target.value) : '')}
              className="ml-6 rounded-md border border-input bg-background px-2 py-1.5 text-sm"
            >
              <option value="">Select a list…</option>
              {lists.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name} ({l.subscriberCount} subscribers)
                </option>
              ))}
            </select>
          )}
        </div>

        <div className="mt-4">
          <Button onClick={() => previewResult && setConfirmingPush(true)} disabled={!canPush || push.isPending}>
            {push.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Push to Brevo List
          </Button>
          {!previewResult && <p className="mt-1 text-xs text-muted-foreground">Run Preview first to confirm the match count before pushing.</p>}
        </div>
        {pushError && <p className="mt-2 text-sm text-destructive">{pushError}</p>}
        {pushSuccessMessage && <p className="mt-2 text-sm text-emerald-700 dark:text-emerald-400">{pushSuccessMessage}</p>}
      </div>

      {showPreview && previewResult && <SmartListPreviewDialog result={previewResult} onClose={() => setShowPreview(false)} />}

      <ConfirmDialog
        open={confirmingPush}
        onOpenChange={setConfirmingPush}
        title="Push to Brevo?"
        description={`This adds ${previewResult?.count ?? 0} matching users to ${pushTarget} in Brevo.`}
        confirmLabel="Push"
        busy={push.isPending}
        onConfirm={doPush}
      />
    </div>
  );
}
